import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Bookmark, ChevronLeft, ChevronRight, Download, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AdminShell, { Pending, StagePill } from "@/components/admin/AdminShell";
import { Card, Empty, Spinner } from "@/components/Common";
import { api, enc, errMsg } from "@/lib/api";
import { SCORE_NOTE, SEGMENTS, STAGES, download, label, usd, when } from "@/lib/admin";

const SORTS = [["intent_score", "Intent"], ["engagement_score", "Engagement"], ["last_activity", "Last activity"], ["resume_score", "Resume score"], ["revenue", "Revenue"], ["registered_at", "Registered"]];
const KEYS = ["q", "stage", "segment", "source", "min_intent", "sort", "order", "page"];
const sel = "h-9 rounded-md border border-[#27272A] bg-[#18181B] px-2 text-xs text-zinc-200";

const Select = ({ v, on, opts, all, testid }) => (
  <select value={v || ""} onChange={(e) => on(e.target.value)} className={sel} data-testid={testid}>
    {all && <option value="">{all}</option>}
    {opts.map((o) => (Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{label(o)}</option>))}
  </select>
);

const SavedFilters = ({ params, apply }) => {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/cc/saved-filters").then((r) => setItems(r.data?.items || r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    const name = window.prompt("Name this filter");
    if (!name) return;
    try { await api.post("/admin/cc/saved-filters", { name, params }); toast.success("Filter saved"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/cc/saved-filters/${enc(id)}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="cc-saved-filters">
      <Button size="sm" variant="outline" onClick={save} data-testid="cc-save-filter-button"><Bookmark className="h-3.5 w-3.5" />Save filter</Button>
      {items.map((f) => (
        <span key={f.id || f.filter_id} className="flex items-center gap-1 rounded-full border border-[#27272A] bg-[#18181B] pl-3 text-xs text-zinc-300">
          <button onClick={() => apply(f.params || {})} data-testid={`cc-saved-filter-${f.id || f.filter_id}`}>{f.name}</button>
          <button onClick={() => del(f.id || f.filter_id)} className="p-1.5 text-zinc-500 hover:text-red-400" aria-label="Delete filter"><Trash2 className="h-3 w-3" /></button>
        </span>
      ))}
    </div>
  );
};

const Row = ({ p }) => (
  <Link to={`/admin/cc/prospects/${enc(p.key)}`} className="grid gap-3 rounded-xl border border-[#27272A] bg-[#09090B] p-4 transition-colors hover:border-[#D4AF37]/40 lg:grid-cols-[1.4fr_0.8fr_0.7fr_1.3fr_0.6fr]" data-testid={`cc-prospect-${p.key}`}>
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2"><span className="truncate text-sm font-medium text-zinc-100">{p.name || p.email}</span>{p.suppressed && <span className="text-[10px] text-red-400">SUPPRESSED</span>}</div>
      <div className="truncate text-xs text-zinc-500">{p.email} · {p.source === "scan_lead" ? "Scan lead" : "Member"}{p.tier ? ` · ${p.tier}` : ""}</div>
      {p.tags?.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{p.tags.map((t) => <span key={t} className="rounded bg-[#18181B] px-1.5 text-[10px] text-zinc-400">{t}</span>)}</div>}
    </div>
    <div className="space-y-1"><StagePill stage={p.stage} /><div className="text-[10px] text-zinc-500">{label(p.segment)}</div></div>
    <div className="font-mono text-xs text-zinc-300">Intent <span className="text-[#F3C649]">{p.intent_score ?? "—"}</span><br />Engage {p.engagement_score ?? "—"}<br />Resume {p.resume_score ?? "—"}</div>
    <div className="text-xs"><div className="text-zinc-200" data-testid={`cc-next-action-${p.key}`}>{p.next_action || "—"}</div>{p.next_action_reason && <div className="text-zinc-500">{p.next_action_reason}</div>}</div>
    <div className="text-right text-xs text-zinc-500">{usd(p.revenue)}<br />{when(p.last_activity)}<br />{p.pricing_views ?? 0} pricing views</div>
  </Link>
);

export default function Prospects() {
  const [sp, setSp] = useSearchParams();
  const params = Object.fromEntries(KEYS.map((k) => [k, sp.get(k) || ""]).filter(([, v]) => v));
  const qs = new URLSearchParams({ sort: "intent_score", order: "desc", page: "1", per_page: "25", ...params }).toString();
  const [d, setD] = useState({ loading: true });
  const [q, setQ] = useState(params.q || "");

  useEffect(() => {
    setD((x) => ({ ...x, loading: true }));
    api.get(`/admin/cc/prospects?${qs}`).then((r) => setD({ data: r.data })).catch((e) => setD({ missing: e.response?.status === 404, error: e.response?.status === 404 ? "" : errMsg(e) }));
  }, [qs]);
  useEffect(() => { const t = setTimeout(() => q !== (params.q || "") && set({ q, page: "" }), 400); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setQ(sp.get("q") || ""); }, [sp]);

  function set(patch) { const n = { ...params, ...patch }; setSp(Object.fromEntries(Object.entries(n).filter(([, v]) => v))); }
  const exportCsv = async () => {
    try {
      const csvQs = new URLSearchParams(qs); csvQs.delete("page"); csvQs.delete("per_page");
      const r = await api.get(`/admin/cc/prospects.csv?${csvQs}`, { responseType: "blob" }); download(r.data, `prospects-${new Date().toISOString().slice(0, 10)}.csv`);
    }
    catch (e) { toast.error(errMsg(e)); }
  };
  const data = d.data || {};
  const page = Number(data.page || params.page || 1), pages = Math.max(1, Math.ceil((data.total || 0) / (data.per_page || 25)));

  return (
    <AdminShell title="Prospects CRM" subtitle={SCORE_NOTE} actions={<Button variant="outline" onClick={exportCsv} disabled={!data.total} data-testid="cc-export-csv"><Download className="h-4 w-4" />Export CSV</Button>}>
      {d.missing && <Pending />}
      <Card className="mb-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="h-9 max-w-xs" data-testid="cc-prospects-search" />
          <Select v={params.stage} on={(v) => set({ stage: v, page: "" })} opts={STAGES} all="All stages" testid="cc-filter-stage" />
          <Select v={params.segment} on={(v) => set({ segment: v, page: "" })} opts={SEGMENTS} all="All segments" testid="cc-filter-segment" />
          <Select v={params.source} on={(v) => set({ source: v, page: "" })} opts={[["member", "Members"], ["scan_lead", "Scan leads"]]} all="All sources" testid="cc-filter-source" />
          <Input type="number" min={0} max={100} value={params.min_intent || ""} onChange={(e) => set({ min_intent: e.target.value, page: "" })} placeholder="Min intent" className="h-9 w-28" data-testid="cc-filter-min-intent" />
          <Select v={params.sort || "intent_score"} on={(v) => set({ sort: v })} opts={SORTS} testid="cc-sort" />
          <Select v={params.order || "desc"} on={(v) => set({ order: v })} opts={[["desc", "High → low"], ["asc", "Low → high"]]} testid="cc-order" />
        </div>
        <SavedFilters params={params} apply={(p) => setSp(p)} />
      </Card>
      {d.loading ? <Spinner className="h-6 w-6 text-[#D4AF37]" /> : d.error ? <p className="text-sm text-red-400">{d.error}</p> : d.missing ? null : !data.items?.length ? <Empty title="No prospects" text="Nothing matches these filters yet." /> : (
        <>
          <p className="mb-2 text-xs text-zinc-500" data-testid="cc-prospects-total">{data.total} prospects</p>
          <div className="space-y-2">{data.items.map((p) => <Row key={p.key} p={p} />)}</div>
          <div className="mt-4 flex items-center justify-end gap-2 text-xs text-zinc-400">
            <Button size="icon" variant="ghost" disabled={page <= 1} onClick={() => set({ page: String(page - 1) })} data-testid="cc-page-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <span data-testid="cc-page">Page {page} of {pages}</span>
            <Button size="icon" variant="ghost" disabled={page >= pages} onClick={() => set({ page: String(page + 1) })} data-testid="cc-page-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </>
      )}
    </AdminShell>
  );
}
